import { useForm, FormProvider, useWatch } from 'react-hook-form';
import { Form, useLoaderData, useNavigate } from 'react-router';
import { useEffect } from 'react';
import PropTypes from 'prop-types';
import SubmitButton from '../core/form/SubmitButton';
import Select from '../core/form/Select';
import { createPathwayForTemplate } from '@/api/pathways';

const AddPathwayToProjectModal = ({ selectedTemplateIds, onClose }) => {
  const { projects } = useLoaderData();
  const navigate = useNavigate();
  const methods = useForm({
    defaultValues: {
      projectOption: 'existing',
      project_id: '',
      project_name: '',
    },
  });

  const { handleSubmit, register, setValue } = methods;
  const projectOption = useWatch({ control: methods.control, name: 'projectOption' });
  const projectId = useWatch({ control: methods.control, name: 'project_id' });
  const projectName = useWatch({ control: methods.control, name: 'project_name' });
  const isFormValid = projectOption && (projectId || projectName?.trim());

  useEffect(() => {
    if (projectOption === 'existing') {
      setValue('project_name', '');
    } else if (projectOption === 'new') {
      setValue('project_id', '');
    }
  }, [projectOption, setValue]);

  const projectOptions =
    projects?.map(project => ({
      value: project.id,
      label: project.name,
    })) || [];

  const onSubmit = async data => {
    const { project_id, project_name } = data;
    const requestData = project_id ? { project_id } : { project_name };
    requestData.template_ids = Array.from(selectedTemplateIds);

    try {
      const response = await createPathwayForTemplate(requestData);
      const projectId = response?.data?.project_id;

      if (projectId) {
        navigate(`/projects/${projectId}`);
      }
    } catch (error) {
      console.error('Error creating pathways:', error);
    }
  };

  return (
    <dialog className="modal" open>
      <div className="modal-box max-w-2xl">
        <FormProvider {...methods}>
          <Form method="post" onSubmit={handleSubmit(onSubmit)}>
            <h3 className="font-bold text-lg mb-6">Add Pathway Templates to Project</h3>

            <div className="mb-6 p-4 border rounded-lg">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input type="radio" value="existing" {...register('projectOption')} className="radio radio-primary" />
                <span className="font-medium text-lg">Add Pathway to Existing Project</span>
              </label>

              <div className="mt-4 ml-6">
                <label className="form-control w-full">
                  <div className="label">
                    <span className="label-text">Project Name</span>
                  </div>
                  <Select
                    name="project_id"
                    options={projectOptions}
                    disabled={projectOption !== 'existing'}
                    className="select-sm"
                  />
                </label>
              </div>
            </div>

            <div className="mb-6 p-4 border rounded-lg">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input type="radio" value="new" {...register('projectOption')} className="radio radio-primary" />
                <span className="font-medium text-lg">Add in New Project</span>
              </label>

              <div className="mt-4 ml-6">
                <label className="form-control w-full">
                  <div className="label">
                    <span className="label-text">Project Name</span>
                  </div>
                  <input
                    type="text"
                    placeholder="Enter Project Name"
                    className="input input-bordered input-sm w-full"
                    disabled={projectOption !== 'new'}
                    {...register('project_name', {
                      required: projectOption === 'new',
                    })}
                  />
                </label>
              </div>
            </div>

            <input type="hidden" name="selectedTemplateIds" value={JSON.stringify(Array.from(selectedTemplateIds))} />

            <div className="modal-action">
              <button type="button" className="btn btn-sm btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <SubmitButton label="Save" isDisabled={!isFormValid} />
            </div>
          </Form>
        </FormProvider>
      </div>
    </dialog>
  );
};

AddPathwayToProjectModal.propTypes = {
  selectedTemplateIds: PropTypes.instanceOf(Set).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AddPathwayToProjectModal;
